import { ChangeDetectionStrategy, Component, Injector, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs';
import { TuiDialogService } from '@taiga-ui/core';
import { TranslateService } from '@ngx-translate/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { ViewMode } from '@src/models';
import { fixRouteParam } from '@src/utils';
import { DialogConfirmComponent } from '@src/app/shared/dialogs';
import { BaseComponent } from '@src/app/modules/base';

@Component({
  selector: 'lp-collections',
  templateUrl: './lp-collections.component.html',
  styleUrls: ['./lp-collections.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LpCollectionsComponent extends BaseComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialogService = inject(TuiDialogService);
  private readonly translateService = inject(TranslateService);
  private readonly injector = inject(Injector);

  selectedCollectionId?: string;
  collectionMode: ViewMode = 'view';
  unionId?: string;
  ref?: string;

  loading = false;

  private readonly confirmCancelEditDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('common.dialogs.cancelHeader'),
      size: 's',
      closeable: false,
    },
  );

  canDeactivate() {
    if (this.collectionMode === 'edit' || this.collectionMode === 'create') {
      this.onCancelEditing();
      return false;
    }

    return true;
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroyed$$)).subscribe(params => {
      this.route.queryParamMap.pipe(takeUntil(this.destroyed$$)).subscribe(queryParams => {
        this.selectedCollectionId = fixRouteParam(params.get('collectionId'));
        this.unionId = fixRouteParam(queryParams.get('unionId'));
        this.ref = fixRouteParam(queryParams.get('ref'));

        if (this.selectedCollectionId) {
          this.collectionMode = 'view';
        }

        this.cdr.markForCheck();
      });
    });
  }

  onCancelEditing() {
    this.confirmCancelEditDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: res => {
        if (res) {
          if (this.collectionMode === 'create') {
            this.selectedCollectionId = undefined;
          }

          this.collectionMode = 'view';
          this.cdr.markForCheck();
        }
      },
    });
  }

  onSaveCollection(id: string) {
    this.collectionMode = 'view';
    this.router.navigate(['loyalty', 'collections', id]);
  }

  onDeleteCollection() {
    this.selectedCollectionId = undefined;
    this.collectionMode = 'view';
    this.router.navigate(['loyalty', 'collections']);
  }

  onSelectCollection(id: string) {
    this.collectionMode = 'view';
    this.router.navigate(['loyalty', 'collections', id]);
  }

  onClickCreateButton() {
    this.selectedCollectionId = undefined;
    this.collectionMode = 'create';
  }

  onClickEditButton() {
    this.collectionMode = 'edit';
  }
}
